import React from 'react';
import Image from 'next/image';
import classnames from 'classnames';
import { Trans, t } from '@lingui/macro';
import { Link } from 'components/link/Link';
import { IImage, ILink } from 'utils/types';
import ChevronRight from 'components/assets/chevron-right.svg';
import { ProductImages } from 'components/product-images/ProductImages';

import s from './ProductIceland.module.scss';

type Props = {
  tagline?: React.ReactNode;
  title?: React.ReactNode;
  description?: React.ReactNode;
  drugType?: React.ReactNode;
  activeIngredient?: React.ReactNode;
  form?: React.ReactNode;
  strengths?: React.ReactNode;
  sizes?: React.ReactNode;
  images?: Array<{
    image: IImage;
  }>;
  manual?: ILink;
  smpc?: ILink;
  warning?: React.ReactNode;
  adNumber?: React.ReactNode;
  links?: { label?: string; link?: ILink }[];
  children?: React.ReactNode;
  heroTitle?: React.ReactNode;
  heroTagline?: React.ReactNode;
  heroImage?: IImage;
  heroTextColor?: boolean;
  heroTextRight?: boolean;
  heroVideo?: ILink;
  heroBackground?: string;
};

export const ProductIceland = ({
  tagline,
  title,
  description,
  drugType,
  activeIngredient,
  form,
  strengths,
  sizes,
  images,
  warning,
  adNumber,
  links,
  heroTitle,
  heroTagline,
  heroImage,
  heroTextColor,
  heroTextRight,
  heroVideo,
  heroBackground,
  children,
}: Props) => {
  const hasVideo = !!heroVideo?.href;

  return (
    <>
      {hasVideo && (
        <div className={s.video} style={{ backgroundColor: heroBackground }}>
          {/* <div className={s.video__activity}>
            <div className={s.video__line}></div>
          </div> */}
          <video
            className={s.video__video}
            poster={heroImage.desktop?.url || heroImage.url}
            src={heroVideo.href}
            autoPlay
            loop
            width="1920"
            height="1080"
            muted
            controls={false}
          />
        </div>
      )}
      {!hasVideo && heroImage && heroImage.url && (
        <figure
          className={classnames(s.hero, {
            [s.dark]: heroTextColor,
            [s.right]: heroTextRight,
            [s.left]: !heroTextRight,
            [s.video]: hasVideo,
          })}
        >
          <div className={s.hero__background} style={{ backgroundColor: heroBackground }}>
            <div className={s.hero__desktop}>
              <Image
                src={heroImage.desktop?.url || heroImage.url}
                layout="fill"
                objectFit="cover"
              />
            </div>
            <div className={s.hero__mobile}>
              <Image src={heroImage.mobile?.url || heroImage.url} layout="fill" objectFit="cover" />
            </div>
          </div>
          <div className={s.product__container}>
            <figcaption className={s.hero__content}>
              {heroTagline && <p className={s.hero__tagline}>{heroTagline}</p>}
              {heroTitle && <p className={s.hero__title}>{heroTitle}</p>}
            </figcaption>
          </div>
        </figure>
      )}
      <article className={s.product}>
        <div className={s.product__container}>
          <div className={s.product__layout}>
            <header className={s.product__header}>
              <p className={s.product__tagline}>{tagline}</p>
              <h1 className={s.product__title}>{title}</h1>
            </header>
            <div className={s.product__main}>
              <div className={s.product__text}>{description}</div>
            </div>
            <div className={s.product__aside}>
              <div className={s.product__gallery}>
                {images && (
                  <ProductImages>
                    {images.map((image) => {
                      if (!image.image) {
                        return null;
                      }

                      return (
                        <Image
                          key={image.image.url}
                          src={image.image.url}
                          width={image.image.width}
                          height={image.image.height}
                          alt={image.image.alt}
                          layout="responsive"
                          objectFit="contain"
                          sizes="960px"
                        />
                      );
                    })}
                  </ProductImages>
                )}
              </div>

              {(drugType || activeIngredient || form || strengths || sizes) && (
                <div className={s.product__details}>
                  <h3 className={s.product__subline}>
                    <Trans>Ítarupplýsingar</Trans>
                  </h3>
                  <dl className={s.product__list}>
                    {drugType && (
                      <div className={s.product__item}>
                        <dt className={s.product__label}>
                          <Trans>Tegund lyfs</Trans>
                        </dt>
                        <dd className={s.product__value}>{drugType}</dd>
                      </div>
                    )}
                    {activeIngredient && (
                      <div className={s.product__item}>
                        <dt className={s.product__label}>
                          <Trans>Virkt innihaldsefni</Trans>
                        </dt>
                        <dd className={s.product__value}>{activeIngredient}</dd>
                      </div>
                    )}
                    {form && (
                      <div className={s.product__item}>
                        <dt className={s.product__label}>
                          <Trans>Lyfjaform</Trans>
                        </dt>
                        <dd className={s.product__value}>{form}</dd>
                      </div>
                    )}
                    {strengths && (
                      <div className={s.product__item}>
                        <dt className={s.product__label}>
                          <Trans>Styrkleiki</Trans>
                        </dt>
                        <dd className={s.product__value}>{strengths}</dd>
                      </div>
                    )}
                    {sizes && (
                      <div className={s.product__item}>
                        <dt className={s.product__label}>
                          <Trans>Magn</Trans>
                        </dt>
                        <dd className={s.product__value}>{sizes}</dd>
                      </div>
                    )}
                  </dl>
                </div>
              )}
              {warning && <div className={s.product__warning}>{warning}</div>}
              {adNumber && <div className={s.product__adNumber}>{adNumber}</div>}
            </div>
            {links && links[0]?.link && (
              <footer className={s.product__footer}>
                <div className={s.product__buttons}>
                  {links &&
                    links.map((link) => {
                      if (link.link?.href) {
                        return (
                          <Link key={link.link.href} {...link.link} className={s.product__button}>
                            {link.label || t`More`} <ChevronRight />
                          </Link>
                        );
                      }
                    })}
                </div>
              </footer>
            )}
          </div>
        </div>
      </article>
    </>
  );
};
